// frontend/src/services/api.js - COMPLETE ENHANCED VERSION WITH MATERIAL UPLOAD SUPPORT
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://universidad-asesores-api.onrender.com/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = null;
    
    // ADD THIS LINE:
    this.initializeToken();
    
    // Debug initialization
    console.log('🔧 ApiService constructor called');
  }

  // File type configurations
  static FILE_TYPES = {
    VIDEO: {
      extensions: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'quicktime', 'x-msvideo', 'x-ms-wmv', 'x-matroska'],
      maxSize: 2 * 1024 * 1024 * 1024, // 2GB
      folder: 'videos'
    },
    MATERIAL: {
      extensions: ['pdf', 'xlsx', 'xls', 'docx', 'doc', 'pptx', 'ppt', 'txt', 'csv'],
      maxSize: 50 * 1024 * 1024, // 50MB
      folder: 'materials'
    }
  };

  initializeToken() {
    try {
      const storedToken = localStorage.getItem('authToken');
      console.log('🔍 Raw stored token:', storedToken);
      
      // Validate token format
      if (storedToken && 
          storedToken !== 'null' && 
          storedToken !== 'undefined' && 
          storedToken.length > 10 &&
          storedToken.includes('.')) {
        
        this.token = storedToken;
        console.log('✅ Valid token loaded from localStorage');
      } else {
        console.log('❌ Invalid or missing token in localStorage');
        this.token = null;
        // Clean up invalid token
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('❌ Token initialization error:', error);
      this.token = null;
    }
  }

  setToken(token) {
    console.log('🔑 setToken called with:', token ? 'token present' : 'no token');
    
    if (token && typeof token === 'string' && token.length > 10) {
      console.log('✅ Setting valid token');
      console.log('🔍 Token preview:', token.substring(0, 20) + '...');
      
      this.token = token;
      localStorage.setItem('authToken', token);
      
      // Verify storage
      const stored = localStorage.getItem('authToken');
      if (stored === token) {
        console.log('✅ Token stored successfully');
      } else {
        console.error('❌ Token storage failed!');
      }
    } else {
      console.log('❌ Invalid token provided to setToken');
      this.clearToken();
    }
  }

  clearToken() {
    console.log('🗑️ Clearing API token');
    this.token = null;
    localStorage.removeItem('authToken');
    
    // Also clear other auth-related data
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('is_admin');
    localStorage.removeItem('is_first_login');
  }

  getStoredToken() {
    return localStorage.getItem('authToken');
  }

  // Enhanced file validation
  validateFile(file, type = 'MATERIAL') {
    const config = ApiService.FILE_TYPES[type];
    const errors = [];
    
    if (!file) {
      return { isValid: false, errors: ['No file selected'] };
    }
    
    // Check file size
    if (file.size > config.maxSize) {
      errors.push(`File size exceeds ${this.formatFileSize(config.maxSize)} limit`);
    }
    
    // Check file extension
    const extension = file.name.split('.').pop().toLowerCase();
    if (!config.extensions.includes(extension)) {
      errors.push(`Invalid file type. Allowed: ${config.extensions.join(', ')}`);
    }
    
    // Check file name length
    if (file.name.length > 100) {
      errors.push('File name too long (max 100 characters)');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      extension,
      category: this.getFileCategory(extension)
    };
  }

  // Get file category for organization
  getFileCategory(extension) {
    const categories = {
      pdf: 'pdf',
      xlsx: 'excel', xls: 'excel', csv: 'excel',
      docx: 'word', doc: 'word',
      pptx: 'powerpoint', ppt: 'powerpoint',
      txt: 'text'
    };
    return categories[extension] || 'other';
  }

  // Format file size for display
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Enhanced headers with multipart support
  getHeaders(isMultipart = false) {
    const headers = {};
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    if (!isMultipart) {
      headers['Content-Type'] = 'application/json';
    }
    
    return headers;
  }

  async request(endpoint, options = {}) {
    // Always refresh token from localStorage before requests
    if (!this.token) {
      console.log('🔄 No token in memory, checking localStorage...');
      this.initializeToken();
    }

    const url = `${this.baseURL}${endpoint}`;
    
    console.log(`🌐 API Request: ${options.method || 'GET'} ${endpoint}`);
    console.log('🔑 Token status:', this.token ? 'Present' : 'Missing');
    
    if (this.token) {
      console.log('🔍 Token preview:', this.token.substring(0, 20) + '...');
    }
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authorization header if token exists
    if (this.token) {
      config.headers.Authorization = `Bearer ${this.token}`;
      console.log('✅ Authorization header added');
    } else {
      console.log('❌ No token available for request');
    }

    try {
      const response = await fetch(url, config);
      
      console.log(`📡 Response: ${response.status} ${response.statusText}`);
      
      // Handle different response types
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 401) {
          console.error('🚫 401 Unauthorized - token might be invalid');
          console.log('🔍 Current token:', this.token ? 'exists' : 'missing');
          
          // Only clear token if it's actually an auth failure, not missing token
          if (this.token) {
            console.log('🗑️ Clearing invalid token');
            this.clearToken();
          }
          
          // Don't auto-redirect during API calls, let components handle it
          const errorMessage = data?.error || data?.message || 'Authentication failed';
          throw new Error(errorMessage);
        }
        
        const errorMessage = data?.error || data?.message || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      console.log(`✅ Request successful: ${endpoint}`);
      return data;
    } catch (error) {
      console.error(`❌ API Error (${endpoint}):`, error.message);
      throw error;
    }
  }

  // Generic API request method for multipart uploads
  async makeRequest(endpoint, options = {}) {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: {
          ...this.getHeaders(options.isMultipart),
          ...options.headers
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // Authentication methods
  async login(username, password) {
    console.log('🔐 Starting login process for:', username);
    
    try {
      const response = await this.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });

      console.log('📦 Login response received:', response);

      if (response.access_token) {
        console.log('✅ Access token received in response');
        
        // Validate token format before storing
        if (typeof response.access_token === 'string' && 
            response.access_token.length > 10 && 
            response.access_token.includes('.')) {
          
          this.setToken(response.access_token);
          console.log('✅ Login successful, token stored');
        } else {
          console.error('❌ Invalid token format received');
          throw new Error('Invalid token format received from server');
        }
      } else {
        console.error('❌ No access token in login response');
        throw new Error('No access token received from server');
      }

      return response;
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  }

  async setPassword(password) {
    console.log('🔑 Setting password via API');
    return this.request('/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ 
        current_password: currentPassword, 
        new_password: newPassword 
      }),
    });
  }

  async testAuth() {
    console.log('🧪 Testing authentication...');
    return this.request('/auth/test');
  }

  async getCurrentUser() {
    console.log('👤 Getting current user...');
    return this.request('/auth/profile');
  }

  logout() {
    console.log('🚪 Logout via API service');
    this.clearToken();
    return Promise.resolve();
  }

  // Token validation
  isTokenExpired() {
    if (!this.token) return true;
    
    try {
      const parts = this.token.split('.');
      if (parts.length !== 3) return true;
      
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return true;
    }
  }

  // Debug method
  debugToken() {
    console.log('=== TOKEN DEBUG INFO ===');
    console.log('Memory token:', this.token ? 'Present' : 'Missing');
    console.log('Storage token:', localStorage.getItem('authToken') ? 'Present' : 'Missing');
    console.log('Token expired:', this.isTokenExpired());
    if (this.token) {
      console.log('Token preview:', this.token.substring(0, 30) + '...');
    }
  }

  // CHAPTER MATERIALS API METHODS

  async getChapterMaterials(chapterId) {
    return this.request(`/chapters/${chapterId}/materials`);
  }


  
  // Upload material to chapter
  async uploadMaterial(chapterId, file, options = {}) {
    if (!this.token) {
      this.initializeToken();
    }

    // Handle different parameter formats for backward compatibility
    let title, description, onProgress;
    
    if (typeof options === 'string') {
      // Legacy format: uploadMaterial(chapterId, file, title, description, onProgress)
      title = options;
      description = arguments[3] || '';
      onProgress = arguments[4];
    } else {
      // New format: uploadMaterial(chapterId, file, { title, description, onProgress, ... })
      title = options.title || file.name;
      description = options.description || '';
      onProgress = options.onProgress;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    
    // Add additional metadata if provided
    if (options.type) formData.append('type', options.type);
    if (options.is_primary !== undefined) formData.append('is_primary', options.is_primary);
    if (options.order_index !== undefined) formData.append('order_index', options.order_index);

    const url = `${this.baseURL}/chapters/${chapterId}/materials`;
    
    console.log('📄 Uploading material for chapter:', chapterId);
    console.log('📊 File details:', {
      name: file.name,
      size: this.formatFileSize(file.size),
      type: file.type
    });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('✅ Material upload successful:', response);
            resolve(response);
          } catch (error) {
            reject(new Error('Invalid response format'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(new Error(errorResponse.error || errorResponse.message || 'Material upload failed'));
          } catch (error) {
            reject(new Error(xhr.responseText || 'Material upload failed'));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during material upload'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Material upload timeout'));
      });

      xhr.open('POST', url);
      
      // Set timeout for large file uploads (10 minutes)
      xhr.timeout = 10 * 60 * 1000;
      
      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }

      xhr.send(formData);
    });
  }



  async uploadChapterMaterial(chapterId, file, materialData = {}) {
    return this.uploadMaterial(chapterId, file, {
      title: materialData.title || file.name,
      description: materialData.description || '',
      type: materialData.type,
      is_primary: materialData.is_primary || false,
      order_index: materialData.order_index || 0,
      onProgress: materialData.onProgress
    });
  }


  // Delete material
  async deleteMaterial(chapterId, materialId) {
    return this.request(`/chapters/${chapterId}/materials/${materialId}`, {
      method: 'DELETE',
    });
  }

  getMaterialUrl(filename) {
    if (!filename) return null;
    return `${this.baseURL}/uploads/materials/${filename}`;
  }

  getChapterMaterialDownloadUrl(chapterId, materialId) {
    return `${this.baseURL}/chapters/${chapterId}/materials/${materialId}/download`;
  }

  // Get material download URL
  async getMaterialDownloadUrl(materialId) {
    console.log('📄 Getting download URL for material:', materialId);
    try {
      const response = await this.request(`/materials/${materialId}/url`);
      // Return full URL for download
      return `${this.baseURL}${response.download_url}`;
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }

  
  // Enhanced video upload method (update existing one)
  async uploadVideoToChapter(chapterId, videoFile, onProgress) {
    // This is just renaming your existing uploadVideo method for consistency
    return this.uploadVideo(chapterId, videoFile, onProgress);
  }

  async updateChapterMaterial(materialId, updates) {
    return this.request(`/materials/${materialId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async deleteChapterMaterial(chapterId, materialId) {
    console.log(`🗑️ Deleting material ${materialId} from chapter ${chapterId}...`);
    return this.request(`/chapters/${chapterId}/materials/${materialId}`, {
      method: 'DELETE',
    });
  }

  async downloadMaterial(materialId) {
    try {
      const response = await fetch(`${this.baseURL}/materials/${materialId}/download`, {
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'download';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Track download
      await this.trackMaterialDownload(materialId);

      return { success: true, filename };
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  async trackMaterialDownload(materialId) {
    return this.request(`/materials/${materialId}/track-download`, {
      method: 'POST'
    });
  }

  // Enhanced chapter methods
  async getChapterWithMaterials(chapterId) {
    return this.request(`/chapters/${chapterId}?include_materials=true`);
  }

  async updateChapterContentType(chapterId, contentType) {
    return this.request(`/chapters/${chapterId}/content-type`, {
      method: 'PUT',
      body: JSON.stringify({ content_type: contentType })
    });
  }

  // Enhanced module methods with material counts
  async getModulesWithMaterialCounts() {
    return this.request('/modules?include_material_counts=true');
  }

  async getModuleWithMaterials(moduleId) {
    return this.request(`/modules/${moduleId}?include_materials=true`);
  }

  // Module methods
  async getModules() {
    console.log('📚 Getting modules...');
    return this.request('/modules');
  }

  async createModule(moduleData) {
    console.log('📝 Creating module:', moduleData);
    return this.request('/modules', {
      method: 'POST',
      body: JSON.stringify(moduleData),
    });
  }

  async updateModule(moduleId, moduleData) {
    return this.request(`/modules/${moduleId}`, {
      method: 'PUT',
      body: JSON.stringify(moduleData),
    });
  }

  async deleteModule(moduleId) {
    return this.request(`/modules/${moduleId}`, {
      method: 'DELETE',
    });
  }

  // Chapter methods
  async getChapters(moduleId) {
    return this.request(`/modules/${moduleId}/chapters`);
  }

  async getModuleChapters(moduleId) {
    console.log('📚 Getting module chapters (using getChapters alias)...');
    return this.getChapters(moduleId);
  }

  async createChapter(chapterData) {
    return this.request('/chapters', {
      method: 'POST',
      body: JSON.stringify(chapterData),
    });
  }

  async updateChapter(chapterId, chapterData) {
    return this.request(`/chapters/${chapterId}`, {
      method: 'PUT',
      body: JSON.stringify(chapterData),
    });
  }

  async deleteChapter(chapterId) {
    return this.request(`/chapters/${chapterId}`, {
      method: 'DELETE',
    });
  }

  // Enhanced Progress tracking methods
  async updateVideoProgress(chapterId, progressData) {
    console.log('📊 Updating video progress:', { chapterId, progressData });
    
    return this.request(`/chapters/${chapterId}/update-progress`, {
      method: 'POST',
      body: JSON.stringify({
        currentTime: progressData.currentTime,
        duration: progressData.duration,
        timestamp: new Date().toISOString()
      }),
    });
  }

  async getChapterProgress(chapterId) {
    console.log('📈 Getting chapter progress for:', chapterId);
    
    return this.request(`/chapters/${chapterId}/progress`);
  }

  async markChapterComplete(chapterId) {
    console.log('✅ Marking chapter complete:', chapterId);
    
    return this.request(`/chapters/${chapterId}/mark-complete`, {
      method: 'POST',
    });
  }

  // Legacy progress method (keeping for compatibility)
  async updateProgress(chapterId, progressData) {
    return this.updateVideoProgress(chapterId, progressData);
  }

  // User methods - ENHANCED WITH BETTER ERROR HANDLING
  async getUsers() {
    console.log('👥 Getting users...');
    try {
      const response = await this.request('/users');
      console.log('✅ Users retrieved successfully:', response?.length || 0, 'users');
      return response;
    } catch (error) {
      console.error('❌ Failed to get users:', error);
      throw error;
    }
  }

  async createUser(userData) {
    console.log('👤 Creating user:', userData);
    try {
      const response = await this.request('/users', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      console.log('✅ User created successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to create user:', error);
      throw error;
    }
  }

  async updateUser(userId, userData) {
    console.log('✏️ Updating user:', userId, userData);
    try {
      const response = await this.request(`/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
      console.log('✅ User updated successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to update user:', error);
      throw error;
    }
  }

  async deleteUser(userId) {
    console.log('🗑️ Deleting user:', userId);
    try {
      const response = await this.request(`/users/${userId}`, {
        method: 'DELETE',
      });
      console.log('✅ User deleted successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to delete user:', error);
      throw error;
    }
  }

  // Enhanced user password management
  async updateUserPassword(userId, passwordData) {
    return this.request(`/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  }

  async resetUserPassword(userId, newPassword) {
    console.log('🔑 Resetting password for user:', userId);
    try {
      const response = await this.request(`/users/${userId}/password`, {
        method: 'PUT',
        body: JSON.stringify({ new_password: newPassword }),
      });
      console.log('✅ Password reset successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to reset password:', error);
      throw error;
    }
  }

  // Video management methods - ENHANCED FOR LARGE FILES
  async uploadVideo(chapterId, videoFile, onProgress) {
    if (!this.token) {
      this.initializeToken();
    }

    const formData = new FormData();
    formData.append('video', videoFile);

    const url = `${this.baseURL}/chapters/${chapterId}/upload-video`;
    
    console.log('📹 Uploading video for chapter:', chapterId);
    console.log('📊 File size:', this.formatFileSize(videoFile.size));

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
          
          // Log progress for large files
          if (videoFile.size > 100 * 1024 * 1024) { // > 100MB
            console.log(`📊 Upload progress: ${percentComplete.toFixed(1)}% (${this.formatFileSize(event.loaded)} / ${this.formatFileSize(event.total)})`);
          }
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('✅ Upload successful:', response);
            resolve(response);
          } catch (error) {
            console.error('❌ Invalid response format:', xhr.responseText);
            reject(new Error('Invalid response format'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(new Error(errorResponse.error || 'Upload failed'));
          } catch (error) {
            reject(new Error(xhr.responseText || 'Upload failed'));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout - file may be too large or connection too slow'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      xhr.open('POST', url);
      
      // ENHANCED: Longer timeout for large files (30 minutes)
      xhr.timeout = 30 * 60 * 1000; // 30 minutes
      
      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }

      console.log(`🚀 Starting upload with ${(xhr.timeout / 1000 / 60).toFixed(0)} minute timeout...`);
      xhr.send(formData);
    });
  }

  // Get video URL for playback
  getVideoUrl(videoUrl) {
    if (!videoUrl) return null;
    
    // Check if it's already a complete URL (Backblaze B2, Cloudinary, etc.)
    if (videoUrl.startsWith('http://') || videoUrl.startsWith('https://')) {
      console.log('🎥 Using direct video URL:', videoUrl);
      return videoUrl;
    }
    
    // Otherwise, it's a legacy filename - construct the upload path
    console.log('🎥 Using legacy upload path for:', videoUrl);
    return `${this.baseURL}/uploads/${videoUrl}`;
  }

  // Delete video from chapter
  async deleteVideo(chapterId) {
    return this.request(`/chapters/${chapterId}/video`, {
      method: 'DELETE',
    });
  }

  // Analytics methods
  async getAnalyticsOverview() {
    console.log('📊 Getting analytics overview...');
    try {
      const response = await this.request('/analytics/overview');
      console.log('✅ Analytics retrieved successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to get analytics:', error);
      // Return empty analytics object on error
      return {
        total_users: 0,
        total_modules: 0,
        total_chapters: 0,
        active_users: 0
      };
    }
  }

  // Analytics methods
  async getAnalyticsOverview() {
    console.log('📊 Getting analytics overview...');
    try {
      const response = await this.request('/analytics/overview');
      console.log('✅ Analytics retrieved successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to get analytics:', error);
      // Return empty analytics object on error
      return {
        total_users: 0,
        total_modules: 0,
        total_chapters: 0,
        active_users: 0
      };
    }
  }

  async getUserProgress() {
    console.log('📊 Getting user progress data...');
    try {
      // Changed from /admin/user-progress to /analytics/user-progress
      const response = await this.request('/analytics/user-progress');
      console.log('✅ User progress retrieved successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to get user progress:', error);
      // Return empty progress object on error
      return {};
    }
  }

  // Optional: Add method for detailed progress
  async getDetailedUserProgress() {
    console.log('📊 Getting detailed user progress data...');
    try {
      const response = await this.request('/analytics/detailed-user-progress');
      console.log('✅ Detailed user progress retrieved successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to get detailed user progress:', error);
      return [];
    }
  }

  // Optional: Add method for single user progress
  async getSingleUserProgress(userId) {
    console.log(`📊 Getting progress for user ${userId}...`);
    try {
      const response = await this.request(`/analytics/user/${userId}/progress`);
      console.log('✅ User progress retrieved successfully');
      return response;
    } catch (error) {
      console.error('❌ Failed to get user progress:', error);
      throw error;
    }
  }

  async getUserProgressAnalytics() {
    return this.request('/analytics/user-progress');
  }

  async getModuleCompletionAnalytics() {
    return this.request('/analytics/module-completion');
  }

  // Enhanced analytics methods for dashboard
  async getCompletionAnalytics() {
    try {
      return await this.request('/analytics/completion');
    } catch (error) {
      console.warn('Completion analytics not available:', error);
      return {
        total_users: 0,
        total_modules: 0,
        overall_completion_rate: 0,
        active_users: 0
      };
    }
  }

  async getModuleProgress(moduleId) {
    try {
      return await this.request(`/analytics/module/${moduleId}/progress`);
    } catch (error) {
      console.warn(`Module ${moduleId} progress not available:`, error);
      return {
        completion_percentage: 0,
        enrolled_users: 0,
        completed_users: 0
      };
    }
  }

  // Material analytics
  async getMaterialAnalytics() {
    return this.request('/admin/analytics/materials');
  }

  async getUserMaterialProgress(userId) {
    return this.request(`/admin/users/${userId}/material-progress`);
  }

  // Material search functionality
  async searchMaterials(query, filters = {}) {
    const params = new URLSearchParams({
      q: query,
      ...filters
    });
    return this.request(`/materials/search?${params}`);
  }

  // Bulk operations for admin
  async bulkDeleteMaterials(materialIds) {
    return this.request('/admin/materials/bulk-delete', {
      method: 'DELETE',
      body: JSON.stringify({ material_ids: materialIds })
    });
  }

  async reorderMaterials(chapterId, materialOrders) {
    return this.request(`/chapters/${chapterId}/materials/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ orders: materialOrders })
    });
  }

  // Admin utility methods
  async resetUsers() {
    return this.request('/admin/reset-users', {
      method: 'POST',
    });
  }

  async getSystemInfo() {
    return this.request('/admin/system-info');
  }

  // Enhanced auth testing
  async testAuthComprehensive() {
    console.log('🧪 Running comprehensive auth test...');
    return this.request('/debug/auth-test-comprehensive');
  }

  async getJwtInfo() {
    console.log('🔍 Getting JWT token info...');
    return this.request('/debug/jwt-info');
  }

  async verifyAdmin() {
    console.log('🔍 Verifying admin status...');
    return this.request('/debug/verify-admin');
  }

  // Test video serving
  async testVideoUrl(filename) {
    const url = this.getVideoUrl(filename);
    console.log('🎥 Testing video URL:', url);
    
    try {
      const response = await fetch(url, { 
        method: 'HEAD',
        headers: this.token ? { 'Authorization': `Bearer ${this.token}` } : {}
      });
      
      console.log('📊 Video URL test result:', {
        url,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      return {
        accessible: response.ok,
        status: response.status,
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length')
      };
    } catch (error) {
      console.error('❌ Video URL test failed:', error);
      return { accessible: false, error: error.message };
    }
  }

  // Enhanced chapter fetch with video validation
  async getChaptersWithVideoValidation(moduleId) {
    console.log('📚 Fetching chapters with video validation for module:', moduleId);
    
    const chapters = await this.getChapters(moduleId);
    
    // Test each video URL
    for (const chapter of chapters) {
      if (chapter.video_filename) {
        console.log(`🎬 Validating video for chapter ${chapter.id}: ${chapter.video_filename}`);
        const videoTest = await this.testVideoUrl(chapter.video_filename);
        chapter.videoAccessible = videoTest.accessible;
        chapter.videoTestResult = videoTest;
        
        if (!videoTest.accessible) {
          console.warn(`⚠️ Video not accessible for chapter ${chapter.id}:`, videoTest);
        }
      }
    }
    
    return chapters;
  }

  // Debug function to check upload directory and files
  async debugUploadInfo() {
    try {
      const response = await this.request('/debug/upload-info');
      console.log('📁 Upload directory info:', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to get upload info:', error);
      return null;
    }
  }

  // File validation helpers - UPDATED FOR LARGER FILES
  validateVideoFile(file) {
    const allowedTypes = [
      'video/mp4',
      'video/avi', 
      'video/mov',
      'video/wmv',
      'video/webm',
      'video/quicktime',
      'video/x-msvideo', // AVI
      'video/x-ms-wmv',  // WMV
      'video/x-matroska' // MKV
    ];
    
    // INCREASED: Maximum file size to 2GB for 1-hour videos
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    
    const errors = [];
    
    if (!file) {
      errors.push('No file selected');
      return { isValid: false, errors };
    }
    
    if (!allowedTypes.includes(file.type)) {
      errors.push('Invalid file type. Please use MP4, AVI, MOV, WMV, WEBM, or MKV');
    }
    
    if (file.size > maxSize) {
      const fileSizeGB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
      const maxSizeGB = (maxSize / (1024 * 1024 * 1024)).toFixed(1);
      errors.push(`File size too large. Maximum size is ${maxSizeGB}GB. Your file is ${fileSizeGB}GB`);
    }
    
    if (file.size === 0) {
      errors.push('File appears to be empty');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      fileInfo: {
        name: file.name,
        size: file.size,
        type: file.type,
        sizeFormatted: this.formatFileSize(file.size),
        estimatedUploadTime: this.estimateUploadTime(file.size)
      }
    };
  }

  validateMaterialFile(file) {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];
    
    const maxSize = 50 * 1024 * 1024; // 50MB
    
    const errors = [];
    
    if (!file) {
      errors.push('No file selected');
      return { isValid: false, errors };
    }
    
    if (!allowedTypes.includes(file.type)) {
      errors.push('Invalid file type. Please use PDF, Word, Excel, PowerPoint, images, or text files');
    }
    
    if (file.size > maxSize) {
      errors.push(`File size too large. Maximum size is ${this.formatFileSize(maxSize)}`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Helper to estimate upload time
  estimateUploadTime(bytes) {
    // Assuming average upload speed of 5 Mbps (conservative estimate)
    const avgSpeedBytesPerSecond = (5 * 1024 * 1024) / 8; // 5 Mbps in bytes per second
    const estimatedSeconds = bytes / avgSpeedBytesPerSecond;
    
    const minutes = Math.ceil(estimatedSeconds / 60);
    
    if (minutes < 1) {
      return 'Less than 1 minute';
    } else if (minutes < 60) {
      return `~${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `~${hours}h ${remainingMinutes}min`;
    }
  }

  // Generate username preview
  generateUsernamePreview(firstName, lastName) {
    if (!firstName || !lastName) return '';
    
    // Take first letter of first name + full last name, lowercase
    const username = (firstName[0] + lastName).toLowerCase();
    
    // Remove special characters and spaces
    return username.replace(/[^a-z0-9]/g, '');
  }

  // Health check
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      return {
        status: response.status,
        ok: response.ok,
        data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        status: 'error',
        ok: false,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  // Connection test
  async testConnection() {
    console.log('🔗 Testing API connection...');
    
    try {
      const health = await this.healthCheck();
      
      if (health.ok) {
        console.log('✅ API connection successful');
        return { success: true, ...health };
      } else {
        console.log('❌ API connection failed');
        return { success: false, ...health };
      }
    } catch (error) {
      console.error('❌ Connection test failed:', error);
      return { 
        success: false, 
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }
}

// Video progress manager class
class VideoProgressManager {
  constructor(apiService) {
    this.apiService = apiService;
    this.progressData = {};
    this.saveInterval = null;
    this.lastSaveTime = 0;
    this.saveFrequency = 10000; // Save every 10 seconds
    
    // Debounced progress update
    this.debouncedUpdate = this.debounce((chapterId, data) => {
      this.updateProgress(chapterId, data);
    }, 2000);
  }
  
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  startTracking(chapterId, videoDuration) {
    console.log(`🎬 Starting progress tracking for chapter ${chapterId}`);
    
    this.progressData[chapterId] = {
      currentTime: 0,
      duration: videoDuration,
      lastUpdate: Date.now()
    };
    
    // Start periodic saving
    this.saveInterval = setInterval(() => {
      this.saveProgress(chapterId);
    }, this.saveFrequency);
  }
  
  updateTime(chapterId, currentTime) {
    if (this.progressData[chapterId]) {
      this.progressData[chapterId].currentTime = currentTime;
      this.progressData[chapterId].lastUpdate = Date.now();
      
      // Debounced update to backend
      this.debouncedUpdate(chapterId, this.progressData[chapterId]);
    }
  }
  
  async updateProgress(chapterId, data) {
    try {
      await this.apiService.updateVideoProgress(chapterId, {
        currentTime: data.currentTime,
        duration: data.duration
      });
      
      console.log(`📊 Progress saved for chapter ${chapterId}: ${Math.round(data.currentTime)}s`);
    } catch (error) {
      console.error('❌ Failed to save progress:', error);
    }
  }
  
  async saveProgress(chapterId) {
    const now = Date.now();
    if (now - this.lastSaveTime < this.saveFrequency - 1000) {
      return; // Too frequent, skip
    }
    
    if (this.progressData[chapterId]) {
      await this.updateProgress(chapterId, this.progressData[chapterId]);
      this.lastSaveTime = now;
    }
  }
  
  async loadProgress(chapterId) {
    try {
      const progress = await this.apiService.getChapterProgress(chapterId);
      console.log(`📥 Loaded progress for chapter ${chapterId}:`, progress);
      return progress;
    } catch (error) {
      console.error('❌ Failed to load progress:', error);
      return { watch_time: 0, completed: false };
    }
  }
  
  async markComplete(chapterId) {
    try {
      console.log(`✅ Marking chapter ${chapterId} as complete`);
      
      // Save final progress first
      if (this.progressData[chapterId]) {
        await this.saveProgress(chapterId);
      }
      
      // Mark as complete
      const result = await this.apiService.markChapterComplete(chapterId);
      console.log('✅ Chapter marked as complete:', result);
      
      return result;
    } catch (error) {
      console.error('❌ Failed to mark chapter complete:', error);
      throw error;
    }
  }
  
  stopTracking(chapterId) {
    console.log(`⏹️ Stopping progress tracking for chapter ${chapterId}`);
    
    // Final save
    if (this.progressData[chapterId]) {
      this.saveProgress(chapterId);
    }
    
    // Cleanup
    if (this.saveInterval) {
      clearInterval(this.saveInterval);
      this.saveInterval = null;
    }
    
    delete this.progressData[chapterId];
  }
  
  getProgressPercentage(chapterId) {
    const data = this.progressData[chapterId];
    if (!data || !data.duration) return 0;
    
    return Math.round((data.currentTime / data.duration) * 100);
  }
}

// Create and export singleton instance
const apiService = new ApiService();
const videoProgressManager = new VideoProgressManager(apiService);

// Debug helper - expose to window for debugging
if (typeof window !== 'undefined') {
  window.apiService = apiService;
  window.videoProgressManager = videoProgressManager;
  
  // Enhanced debug functions
  window.debugAuth = () => {
    console.log('=== AUTH DEBUG ===');
    console.log('Token in memory:', apiService.token ? 'Yes' : 'No');
    console.log('Token in storage:', localStorage.getItem('authToken') ? 'Yes' : 'No');
    console.log('Storage token:', localStorage.getItem('authToken'));
    apiService.debugToken();
  };
  
  window.testAPI = async () => {
    console.log('=== API TEST ===');
    const result = await apiService.testConnection();
    console.log('Connection test result:', result);
    return result;
  };
  
  window.clearAuth = () => {
    console.log('=== CLEARING AUTH ===');
    apiService.clearToken();
    console.log('Auth cleared');
  };
  
  // Video validation helper
  window.testVideoFile = (file) => {
    const validation = apiService.validateVideoFile(file);
    console.log('Video validation result:', validation);
    return validation;
  };

  // Material validation helper
  window.testMaterialFile = (file) => {
    const validation = apiService.validateMaterialFile(file);
    console.log('Material validation result:', validation);
    return validation;
  };
  
  window.testProgress = async (chapterId) => {
    console.log('=== PROGRESS TEST ===');
    
    // Start tracking
    videoProgressManager.startTracking(chapterId, 300); // 5 minute video
    
    // Simulate progress
    videoProgressManager.updateTime(chapterId, 30);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    videoProgressManager.updateTime(chapterId, 60);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Load progress
    const progress = await videoProgressManager.loadProgress(chapterId);
    console.log('Current progress:', progress);
    
    // Stop tracking
    videoProgressManager.stopTracking(chapterId);
  };

  // Test users endpoint specifically
  window.testUsers = async () => {
    console.log('=== USERS ENDPOINT TEST ===');
    try {
      console.log('Testing authentication first...');
      apiService.debugToken();
      
      console.log('Making request to /users...');
      const users = await apiService.getUsers();
      console.log('✅ Users endpoint successful:', users);
      return users;
    } catch (error) {
      console.error('❌ Users endpoint failed:', error);
      return { error: error.message };
    }
  };

  // Test token refresh
  window.refreshToken = () => {
    console.log('=== TOKEN REFRESH TEST ===');
    apiService.initializeToken();
    console.log('Token refreshed from localStorage');
    apiService.debugToken();
  };

  // Test create user
  window.testCreateUser = async (firstName = 'Test', lastName = 'User') => {
    console.log('=== CREATE USER TEST ===');
    try {
      const userData = {
        first_name: firstName,
        last_name: lastName
      };
      
      console.log('Creating user:', userData);
      const result = await apiService.createUser(userData);
      console.log('✅ User creation successful:', result);
      return result;
    } catch (error) {
      console.error('❌ User creation failed:', error);
      return { error: error.message };
    }
  };

  // Test all admin endpoints
  window.testAdminEndpoints = async () => {
    console.log('=== ADMIN ENDPOINTS TEST ===');
    const results = {};
    
    try {
      // Test modules
      console.log('Testing modules...');
      results.modules = await apiService.getModules();
      console.log('✅ Modules:', results.modules?.length || 0);
    } catch (error) {
      console.error('❌ Modules failed:', error);
      results.modules = { error: error.message };
    }
    
    try {
      // Test users
      console.log('Testing users...');
      results.users = await apiService.getUsers();
      console.log('✅ Users:', results.users?.length || 0);
    } catch (error) {
      console.error('❌ Users failed:', error);
      results.users = { error: error.message };
    }
    
    try {
      // Test analytics
      console.log('Testing analytics...');
      results.analytics = await apiService.getAnalyticsOverview();
      console.log('✅ Analytics:', results.analytics);
    } catch (error) {
      console.error('❌ Analytics failed:', error);
      results.analytics = { error: error.message };
    }
    
    return results;
  };

  // Test material endpoints
  window.testMaterialEndpoints = async (chapterId = 1) => {
    console.log('=== MATERIAL ENDPOINTS TEST ===');
    const results = {};
    
    try {
      // Test get chapter materials
      console.log('Testing get chapter materials...');
      results.materials = await apiService.getChapterMaterials(chapterId);
      console.log('✅ Chapter materials:', results.materials?.length || 0);
    } catch (error) {
      console.error('❌ Get materials failed:', error);
      results.materials = { error: error.message };
    }
    
    try {
      // Test material analytics
      console.log('Testing material analytics...');
      results.analytics = await apiService.getMaterialAnalytics();
      console.log('✅ Material analytics:', results.analytics);
    } catch (error) {
      console.error('❌ Material analytics failed:', error);
      results.analytics = { error: error.message };
    }
    
    return results;
  };

  // Direct API call test
  window.testDirectAPI = async (endpoint, method = 'GET', body = null) => {
    console.log(`=== DIRECT API TEST: ${method} ${endpoint} ===`);
    
    const token = localStorage.getItem('authToken');
    console.log('Using token:', token ? token.substring(0, 20) + '...' : 'None');
    
    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
      }
    };
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }
    
    try {
      const response = await fetch(`${apiService.baseURL}${endpoint}`, config);
      const data = await response.json();
      
      console.log('Response status:', response.status);
      console.log('Response data:', data);
      
      return {
        status: response.status,
        ok: response.ok,
        data
      };
    } catch (error) {
      console.error('Direct API test failed:', error);
      return {
        error: error.message
      };
    }
  };

  // Test file upload simulation
  window.testFileUpload = (type = 'material') => {
    console.log(`=== ${type.toUpperCase()} FILE UPLOAD TEST ===`);
    
    // Create a mock file for testing
    const mockFileContent = new Blob(['Test file content'], { type: 'text/plain' });
    const mockFile = new File([mockFileContent], `test-file.${type === 'video' ? 'mp4' : 'pdf'}`, {
      type: type === 'video' ? 'video/mp4' : 'application/pdf'
    });
    
    const validation = type === 'video' 
      ? apiService.validateVideoFile(mockFile)
      : apiService.validateMaterialFile(mockFile);
    
    console.log('File validation result:', validation);
    return { file: mockFile, validation };
  };
}

export default apiService;
export { VideoProgressManager, videoProgressManager };